import { Nullish } from "../types";

function calculateYearAndMonthDiff(startDate: Date, endDate: Date): { yearDiff: number, monthDiff: number } {
  let yearDiff = endDate.getFullYear() - startDate.getFullYear();
  let monthDiff = endDate.getMonth() - startDate.getMonth();

  if (monthDiff < 0) {
    yearDiff -= 1;
    monthDiff += 12;
  }

  return { yearDiff, monthDiff };
}

export function currentCompanyExperience(
  endDateStr: string | null | undefined,
  startDateStr: string | null | undefined,
  isCurrent: boolean | null | undefined,
  isShortVersion: boolean | null | undefined
): string {
  if (!startDateStr && !endDateStr) {
    return "-";
  }

  const currentDate = new Date();
  const startFormatted = startDateStr ? new Date(startDateStr).toLocaleString("default", { month: "short", year: "numeric" }) : "N/A";
  const endFormatted = endDateStr ?
    new Date(endDateStr).toLocaleString("default", { month: "short", year: "numeric" }) :
    isCurrent ?
      'Present' :
      "-";

  if (!startDateStr) {
    return `N/A - ${endFormatted}`;
  }

  else if (!endDateStr ) {
    return `${startFormatted} - Present`;
  }

  else if (currentDate < new Date(endDateStr!)) {
    const { yearDiff, monthDiff } = calculateYearAndMonthDiff(new Date(startDateStr!), currentDate);
    return `${startFormatted} - Present`;
  }

  else {
    const endDate = isCurrent ? currentDate : endDateStr;
    const { yearDiff, monthDiff } = calculateYearAndMonthDiff(new Date(startDateStr), new Date(endDate!));
    const longString = `${startFormatted} - ${endFormatted}`;
    return `${isShortVersion ? '' : longString}`;
  }
}

export function formatCurrentExp(startDate: string | undefined) {
  if ((startDate === null || startDate === undefined)) {
    return "-";
  }
  // Parse the start date
  const startDateObj = new Date(startDate);

  // Get the current date
  const currentDate = new Date();

  // Calculate the difference in years and months
  let yearsDiff = currentDate.getFullYear() - startDateObj.getFullYear();
  let monthsDiff = currentDate.getMonth() - startDateObj.getMonth();

  // Adjust for negative months difference
  if (monthsDiff < 0) {
    yearsDiff--;
    monthsDiff += 12;
  }

  // Format the result
  const formattedDate = `${startDateObj.toLocaleString('default', { month: 'short' })} ${startDateObj.getFullYear()} - Present · ${yearsDiff} Yr ${monthsDiff} Mos`;

  return formattedDate;
}

export function getExperienceString(experience: number) {
  const [years = 0, months = 0] = experience?.toFixed(2)?.split(".", 2)?.map(x => parseInt(x.substring(0, 2), 10));
  let yearsString = years == 0 ? "" : years > 1 ? `${years} yrs, ` : `${years} yr, `;
  let monthsString = months > 1 ? ` mos.` : ` mo.`;
  if (months) {
    return `${yearsString}${months > 11 ? Math.round(months / 10) : months}${monthsString}`;
  } else {
    return `${yearsString.trim()}`;
  }
}

export const getConfidentialSalaryRange = (salary: number) => {
  if (salary >= 1 && salary <= 100000) {
      return "50 K - 1 L";
  } else if (salary >= 100001 && salary <= 500000) {
      return "1 L - 5 L";
  } else if (salary >= 500001 && salary <= 1000000) {
      return "5 L - 10 L";
  } else if (salary >= 1000001 && salary <= 1500000) {
      return "10 L - 15 L";
  } else if (salary >= 1500001 && salary <= 2000000) {
      return "15 L - 20 L";
  } else if (salary >= 2000001 && salary <= 2500000) {
      return "20 L - 25 L";
  } else if (salary >= 2500001 && salary <= 3500000) {
      return "25 L - 35 L";
  } else if (salary >= 3500001 && salary <= 4500000) {
      return "35 L - 45 L";
  } else if (salary >= 4500001 && salary <= 5500000) {
      return "45 L - 55 L";
  } else if (salary >= 5500001 && salary <= 6500000) {
      return "55 L - 65 L";
  } else if (salary >= 6500001 && salary <= 7500000) {
      return "65 L - 75 L";
  } else if (salary >= 7500001 && salary <= 8500000) {
      return "75 L - 85 L";
  } else if (salary >= 8500001 && salary <= 10000000) {
      return "85 L - 1 Cr";
  } else if (salary > 10000000) {
      return "More than 1 Cr";
  } else {
      return "Confidential"; // To handle any invalid or unexpected input.
  }
}

export const BELOW_50_K = 'Below 50 K'; //used on multiple component

export const salaryFormatter = (num :any,isAddString=true,isINRCurrency=false) => {
  let salary = '';
  if(isINRCurrency && (num >= 1 && num<= 99999)  ){
    if(num>=1 && num<=49999){
     return BELOW_50_K;
    }
    else{
       return num?.toString()?.slice(0,2)+ ' K'
    }
}
  if (Math.abs(num) < 9999999) {
      let roundedNumber : any = (Math.abs(num) / 100000).toFixed(2)
      salary =isINRCurrency ?  (Math.sign(num) * roundedNumber).toLocaleString('en-IN'):  (Math.sign(num) * roundedNumber).toLocaleString();
      if(isAddString){
        salary=salary.concat(' L');
      }
  } else if (Math.abs(num) > 9999999) {
      let roundedNumber: any = (Math.abs(num) / 10000000).toFixed(2)
      salary = (Math.sign(num) * roundedNumber).toString();
      if(isAddString){
        salary=salary.concat(' C')
      }
  }
  return salary;
};

export function getEducationString(college: Nullish<string>, degree: Nullish<string>, passing_year: Nullish<number>, specialization: Nullish<string>): Nullish<string> {
  if (!college) return null;

  let _college: Nullish<string> = null,
    _degree: Nullish<string> = null;

  if (typeof college === "string") {
    if (college.toLowerCase() !== "other") {
      _college = college.trim()
    }
  }

  if (typeof degree === "string") {
    _degree = degree.trim();
  }

  const collegeString = _college ? ` at ${_college}` : "";
  const degreeString = _degree ? _degree : (specialization ? specialization : "");
  const degreeYear = passing_year ? `in ${passing_year}` : "";

  if (!collegeString && !degreeString && !degreeYear) return null;

  return `${degreeString}${collegeString} ${degreeYear}`;
}

export function formatDate(dateString: string): string {
  if (typeof dateString === "string") {
    const date = new Date(dateString);
    const [, month, dateOfTheMonth, year] = date.toDateString().split(" ");

    return `${dateOfTheMonth} ${month} ${year}`
  }
  else return ""
}

export function returnFallbackIfEmpty(value: (any[] | any)) {
  const emptyValue = "-";

  if (Array.isArray(value)) {
    return value.length > 0
      ? value
      : [emptyValue]
  }

  if (typeof value === "string") {
    return value || emptyValue
  }

  return value ?? emptyValue
}

interface Mapping {
  mappingId: number;
  stateName: string;
}
export function getStateNameFromMapping(mappingId: number): string {
  const mappingData: Mapping[] = [
    {
      "mappingId": 1,
      "stateName": "Prospect"
    },
    {
      "mappingId": 2,
      "stateName": "Auto-Matched"
    },
    {
      "mappingId": 3,
      "stateName": "Contacted"
    },
    {
      "mappingId": 4,
      "stateName": "SMS"
    },
    {
      "mappingId": 5,
      "stateName": "Chat"
    },
    {
      "mappingId": 6,
      "stateName": "Email"
    },
    {
      "mappingId": 7,
      "stateName": "Responded"
    },
    {
      "mappingId": 8,
      "stateName": "Shortlisted"
    },
    {
      "mappingId": 9,
      "stateName": "Job Applicant"
    },
    {
      "mappingId": 10,
      "stateName": "Applicants"
    },
    {
      "mappingId": 11,
      "stateName": "Recommended"
    },
    {
      "mappingId": 12,
      "stateName": "Interviewed"
    },
    {
      "mappingId": 13,
      "stateName": "Phone Screen"
    },
    {
      "mappingId": 14,
      "stateName": "HM Screen"
    },
    {
      "mappingId": 15,
      "stateName": "Interview"
    },
    {
      "mappingId": 16,
      "stateName": "Offer"
    },
    {
      "mappingId": 17,
      "stateName": "BGV"
    },
    {
      "mappingId": 18,
      "stateName": "Hired"
    },
    {
      "mappingId": 19,
      "stateName": "Removed"
    },
    {
      "mappingId": 20,
      "stateName": "Salary Mismatch"
    },
    {
      "mappingId": 21,
      "stateName": "Not Interested"
    },
    {
      "mappingId": 22,
      "stateName": "Over Qualified"
    },
    {
      "mappingId": 23,
      "stateName": "Under Qualified"
    },
    {
      "mappingId": 24,
      "stateName": "Profile Mismatch"
    },
    {
      "mappingId": 25,
      "stateName": "Position Closed"
    },
    {
      "mappingId": 26,
      "stateName": "Withdrew"
    },
    {
      "mappingId": 27,
      "stateName": "Others"
    },
    {
      "mappingId": 28,
      "stateName": "Applicants"
    },
    {
      "mappingId": 29,
      "stateName": "Inbound Applicants"
    },
    {
      "mappingId": 30,
      "stateName": "Responded"
    },
    {
      "mappingId": 31,
      "stateName": "Responded"
    }
  ]
  const mapping = mappingData.find(item => item.mappingId === mappingId);
  return mapping ? mapping.stateName : "N/A";
}

export function getNoticeText(noticePeriod: number, servingNoticePeriod: boolean, lwd: string,predicatedNoticePeriod:number) {
  // if (noticePeriod === 0) {
  //   return "Immediate Joiner";
  // } else if (noticePeriod > 0) {
  //   return noticePeriod + " days";
  // } else {
  //   return "N/A";
  // }

  if (servingNoticePeriod && lwd) {
    return 'LWD: ' + formatDate(lwd);
  }

  if (noticePeriod !== undefined && noticePeriod != null) {
    if (noticePeriod === 1) {
      return 'Immediate Joiner'
    } else {
      return `${noticePeriod} days`;
    }
  }
  else if (predicatedNoticePeriod){
    return `${predicatedNoticePeriod} days`
  }
  else {
    return '-'
  }
}

// export function addCustomStringForFalse(input1:boolean,input2:boolean,input3:boolean,input3:boolean) {
//   const result = [];

//   for (const input of inputs) {
//       if (input === false) {
//           result.push(customString);
//       } else {
//           result.push(input);
//       }
//   }

//   return result;
// }


export function mergeEmployerIndustryCategory(employments:any[],uuid :string) {
  let allEmployments = employments?.map(employment => {
    if (!uuid || !employment?.uuid || uuid !== employment?.uuid) {
        return  employment?.employer_industry_category_list  || []
    }
    return []
  }
  )
    || []
  allEmployments = [].concat(...allEmployments)
  return [...new Set(allEmployments)]
}

// retru 1 day , 1 week , 1 month or 1 year
export const   timeDifference=(date1:any, date2:any)=> {
    
  date1 = new Date(date1);
  date2 = new Date(date2);
  if (date1 > date2) {
      [date1, date2] = [date2, date1];
  }

  // Calculate the difference in milliseconds
  let diff = date2 - date1;
  const oneDay = 24 * 60 * 60 * 1000;
  const oneWeek = 7 * oneDay;
  const oneMonth = 30 * oneDay;
  const oneYear = 365 * oneDay;

  // Calculate the difference in days
  const diffInDays = Math.floor(diff / oneDay);

  if (diffInDays === 0) {
    return "Today";
} else if (diffInDays < 7) {
    return `${diffInDays} ${diffInDays ==1 ? 'day ago':'days ago'}`;
} else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return `${weeks} ${weeks ==1 ? 'week ago':'weeks ago'}`;
} else if (diffInDays < 365) {
    const months = Math.floor(diffInDays / 30);
    return`${months} ${months ==1 ? 'mon ago':'mos ago'}`;
} else {
    const years = Math.floor(diffInDays / 365);
    return `${years} ${years ==1 ? 'year ago':'years ago'}`;
}
}

export const formatActivityDate = (dateStr: string): string => {
  if(!dateStr) {
    return '-';
  }
  const today = new Date();
  const inputDate = new Date(dateStr);
  const timeDifference = today.getTime() - inputDate.getTime(); // Get time difference in milliseconds
  const daysDifference = Math.floor(timeDifference / (1000 * 3600 * 24)); // Convert to days

  // If the date is within 90 days, format the date as dd mmm yyyy
  if (daysDifference <= 90) {
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
    return inputDate.toLocaleDateString('en-GB', options);
  }

  // If the date is older than 90 days, return relative time (e.g., "2 months ago")
  const monthsDifference = Math.floor(daysDifference / 30);
  const yearsDifference = Math.floor(monthsDifference / 12);

  if (yearsDifference > 0) {
    return `${yearsDifference} year${yearsDifference > 1 ? 's' : ''} ago`;
  } else if (monthsDifference > 0) {
    return `${monthsDifference} mo${monthsDifference > 1 ? 's' : 'n'} ago`;
  } else {
    return `${daysDifference} day${daysDifference > 1 ? 's' : ''} ago`;
  }
};

export const  checkDateWithinSixMonths=(d1:string | undefined)=> {
  if(d1 && d1 !='-' ){
  const currentDate = new Date(); // Get the current date
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(currentDate.getMonth() - 6); // Calculate the date 6 months ago

  const date1 = new Date(d1);
     return date1 >= sixMonthsAgo && date1 <= currentDate
}
return  false
}

